import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  Typography,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Popover from "@mui/material/Popover";
import Chip from "@mui/material/Chip";
import { commonContainerWrapper } from "../../Common/styles/flex";
import React, { useEffect, useState, useMemo } from "react";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import { debounce } from "lodash";
import { HalfInputWrapper, LabelStyle } from "../../Common/styles/form";

type Props = {
  canEdit: boolean;
  studyId?: string;
};

interface EmailTemplate {
  id: string;
  name: string;
  dynamicVariables?: string[];
}

interface EmailEventTemplate {
  id?: string;
  studyId?: string;
  eventName: string;
  isEnabled: boolean;
  templateId?: string;
  template?: EmailTemplate;
}

interface EventWithTemplate {
  value: string;
  label: string;
  variables: string[];
  eventTemplateId: string | null;
  selectedTemplate: EmailTemplate | null;
  isEnabled: boolean;
}

interface Role {
  id: string;
  label: string;
  code: string;
  type: string;
  studyId: string;
  description: string | null;
}

interface RoleBasedTemplateItem {
  id?: string; // eventTemplateId if exists
  template: EmailTemplate | null;
  isEnabled: boolean;
  roleId: string | null;
}

// Study events
const STUDY_EVENTS = [
  { value: "study_admin.welcome_email", label: "Study Admin Welcome Email", variables: ["inviteLink", "studyName"] },
  { value: "appointment.scheduled", label: "Appointment Scheduled", variables: ["appointmentDate", "startTime", "endTime", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "appointment.rescheduled", label: "Appointment Rescheduled", variables: ["appointmentDate", "startTime", "endTime", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "appointment.session_complete", label: "Appointment Session Complete", variables: ["appointmentDate", "startTime", "endTime", "studyName", "firstName", "lastName", "subjectId", "completedAt"] },
  { value: "appointment.session_reminder", label: "Appointment Session Reminder", variables: ["appointmentDate", "startTime", "endTime", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "study_screener.initial_reachout", label: "Study Screener Initial Reachout", variables: ["inviteLink", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "study_screener.followup_reachout", label: "Study Screener Followup Reachout", variables: ["inviteLink", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "study.snowball_recruitment", label: "Study Snowball Recruitment", variables: ["inviteLink", "studyName", "firstName", "lastName", "subjectId"] },
  { value: "study.consent_email", label: "Study Consent Email", variables: ["url", "studyName", "firstName", "lastName", "subjectId", "consentName", "consentVersion"] },
  { value: "study.consent_counter_sign_email", label: "Study Consent Counter Sign Email", variables: ["url", "studyName", "firstName", "lastName", "subjectId", "consentName", "consentVersion"] },
  { value: "study.screener_complete", label: "Study Screener Complete", variables: ["studyName", "firstName", "lastName", "subjectId"] },
  { value: "survey.invite", label: "Survey Invite Email", variables: ["subject", "body", "inviteUrl"] },
];

// Registry events
const REGISTRY_EVENTS = [
  { value: "participant.registration_email", label: "Participant Registration Email", variables: ["link"] },
  { value: "registry.screener_complete", label: "Registry Screener Complete", variables: [] },
  { value: "platform_user.welcome_email", label: "Platform User Welcome Email", variables: ["inviteLink"] },
  { value: "user.password_reset", label: "User Password Reset", variables: ["name", "resetLink"] },
  // { value: "otp.email", label: "OTP Email" },
  // { value: "export.complete", label: "Export Complete" },
  { value: "participant.welcome_email", label: "Participant Welcome Email", variables: ["link"] },
];

const EmailConfig = ({ canEdit, studyId }: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [eventsWithTemplates, setEventsWithTemplates] = useState<
    EventWithTemplate[]
  >([]);
  const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>(
    []
  );
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [savingEvent, setSavingEvent] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleBasedTemplates, setRoleBasedTemplates] = useState<
    Record<string, RoleBasedTemplateItem[]>
  >({});

  // Handle template search with debounce
  const handleTemplateSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          setTemplatesLoading(true);
          let url = `/provider-email-templates/search?provider=sendgrid`;

          if (value) {
            url += `&search=${value}`;
          }
          const res: AxiosResponse = await http.get(url);
          const templateList = res.data?.data?.map((template: any) => {
            return {
              id: template?.id,
              name:
                template?.name || template?.templateName || "Untitled Template",
              dynamicVariables:
                template?.activeVersionContent?.dynamicVariables ?? [],
            };
          });
          setAvailableTemplates(templateList || []);
        } catch (err) {
          errorToastMessage(err as Error);
        } finally {
          setTemplatesLoading(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    handleTemplateSearch("");
  }, [handleTemplateSearch]);

  // Fetch all event templates and merge with event definitions
  useEffect(() => {
    const fetchEventTemplates = async () => {
      try {
        setLoading(true);
        let url: string;
        if (studyId) {
          url = `/admin/study/${studyId}/email-event-templates/all`;
        } else {
          url = `/registry/admin/email-event-templates/all`;
        }

        // Fetch roles if studyId exists
        if (studyId) {
          const rolesRes: AxiosResponse = await http.get(
            `/roles?studyId=${studyId}&type=study`
          );
          setRoles(rolesRes.data?.data || []);
        }

        const [res, searchRes]: AxiosResponse[] = await Promise.all([
          http.get(url),
          http.get(`/provider-email-templates/search?provider=sendgrid`),
        ]);
        const templates = res.data?.data || [];

        // Build lookup from whatever templates are already fetched for the dropdown
        const dynVarsMap: Record<string, string[]> = {};
        (searchRes.data?.data || []).forEach((t: any) => {
          if (t?.id && t?.activeVersionContent?.dynamicVariables) {
            dynVarsMap[t.id] = t.activeVersionContent.dynamicVariables;
          }
        });

        // Separate role-based templates (study_admin.welcome_email with roleId)
        const roleBasedMap: Record<string, RoleBasedTemplateItem[]> = {};
        const regularTemplates: EmailEventTemplate[] = [];

        templates.forEach((template: any) => {
          if (
            template.eventName === "study_admin.welcome_email"
            // &&template.roleId
          ) {
            if (!roleBasedMap[template.eventName]) {
              roleBasedMap[template.eventName] = [];
            }
            roleBasedMap[template.eventName].push({
              id: template.id,
              template: template.template
                ? {
                    id: template.templateId,
                    name: template.template.name,
                  }
                : null,
              isEnabled: template.isEnabled,
              roleId: template.roleId,
            });
          } else {
            regularTemplates.push(template);
          }
        });

        setRoleBasedTemplates(roleBasedMap);

        // Convert array to object keyed by eventName (for non-role-based)
        const templatesMap: Record<string, EmailEventTemplate> = {};
        regularTemplates.forEach((template: EmailEventTemplate) => {
          // Only use first template if multiple exist for same event (non-role-based)
          if (!templatesMap[template.eventName]) {
            templatesMap[template.eventName] = template;
          }
        });

        // Merge event definitions with their template data
        const baseEvents = studyId ? STUDY_EVENTS : REGISTRY_EVENTS;
        const eventsWithData: EventWithTemplate[] = baseEvents.map((event) => {
          // Skip role-based events in regular mapping (they're handled separately)
          if (event.value === "study_admin.welcome_email" && studyId) {
            return {
              value: event.value,
              label: event.label,
              variables: event.variables,
              eventTemplateId: null,
              selectedTemplate: null,
              isEnabled: false,
            };
          }

          const eventTemplate = templatesMap[event.value] || null;
          let selectedTemplate: EmailTemplate | null = null;

          if (eventTemplate?.templateId && eventTemplate?.template) {
            selectedTemplate = {
              id: eventTemplate.templateId,
              name: eventTemplate.template.name,
              dynamicVariables: dynVarsMap[eventTemplate.templateId],
            };
          }

          return {
            value: event.value,
            label: event.label,
            variables: event.variables,
            eventTemplateId: eventTemplate?.id || null,
            selectedTemplate,
            isEnabled: eventTemplate?.isEnabled ?? false,
          };
        });

        setEventsWithTemplates(eventsWithData);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
        setSavingEvent(null);
      }
    };

    fetchEventTemplates();
  }, [studyId, refreshTrigger]);

  const handleSave = async (
    eventName: string,
    template: EmailTemplate | null,
    isEnabled: boolean
  ) => {
    if (!template) {
      errorToastMessage(new Error("Please select a template"));
      return;
    }

    setSavingEvent(eventName);
    try {
      const eventWithTemplate = eventsWithTemplates.find(
        (e) => e.value === eventName
      );
      const eventTemplateId = eventWithTemplate?.eventTemplateId;
      const payload = {
        studyId: studyId || undefined,
        templateId: template.id,
        isEnabled,
      };

      let res: AxiosResponse;
      if (eventTemplateId) {
        // Update existing
        if (studyId) {
          res = await http.put(
            `/admin/study/${studyId}/email-event-templates/${eventTemplateId}`,
            payload
          );
        } else {
          res = await http.put(
            `/registry/admin/email-event-templates/${eventTemplateId}`,
            payload
          );
        }
      } else {
        // Create new
        const createPayload = {
          ...payload,
          eventName,
        };
        if (studyId) {
          res = await http.post(
            `/admin/study/${studyId}/email-event-templates`,
            createPayload
          );
        } else {
          res = await http.post(
            `/registry/admin/email-event-templates`,
            createPayload
          );
        }
      }

      toastMessage(
        "success",
        res?.data?.message || "Template saved successfully"
      );

      // Refresh event templates to get latest data
      setRefreshTrigger((prev) => !prev);
    } catch (err) {
      errorToastMessage(err as Error);
    }
  };

  const handleSaveRoleBased = async (
    eventName: string,
    items: RoleBasedTemplateItem[]
  ) => {
    if (!studyId) {
      errorToastMessage(new Error("Study ID is required"));
      return;
    }

    // Validate all items have required fields
    for (const item of items) {
      if (!item.template) {
        errorToastMessage(new Error("Please select a template for all items"));
        return;
      }
    }

    setSavingEvent(eventName);
    try {
      for (const item of items) {
        const payload = {
          studyId,
          templateId: item.template!.id,
          isEnabled: item.isEnabled,
          roleId: item.roleId || null,
        };

        if (item.id) {
          // Update existing
          await http.put(
            `/admin/study/${studyId}/email-event-templates/${item.id}`,
            payload
          );
        } else {
          // Create new
          const createPayload = {
            ...payload,
            eventName,
          };
          await http.post(
            `/admin/study/${studyId}/email-event-templates`,
            createPayload
          );
        }
      }
      toastMessage("success", "Templates saved successfully");

      // Refresh event templates to get latest data
      setRefreshTrigger((prev) => !prev);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSavingEvent(null);
    }
  };

  return (
    <Box sx={{ ...commonContainerWrapper }}>
      <Typography variant="h2" sx={{ mb: 3 }}>
        Email Configuration
      </Typography>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "150px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {eventsWithTemplates.map((eventWithTemplate) => (
            <EventAccordion
              key={eventWithTemplate.value}
              event={{
                value: eventWithTemplate.value,
                label: eventWithTemplate.label,
              }}
              variables={eventWithTemplate.variables}
              eventTemplateId={eventWithTemplate.eventTemplateId}
              selectedTemplate={eventWithTemplate.selectedTemplate}
              isEnabled={eventWithTemplate.isEnabled}
              availableTemplates={availableTemplates}
              templatesLoading={templatesLoading}
              handleTemplateSearch={handleTemplateSearch}
              canEdit={canEdit}
              savingEvent={savingEvent}
              onSave={handleSave}
              onSaveRoleBased={handleSaveRoleBased}
              studyId={studyId}
              roles={roles}
              roleBasedTemplates={roleBasedTemplates}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type RoleBasedTemplateFormProps = {
  items: RoleBasedTemplateItem[];
  setItems: React.Dispatch<React.SetStateAction<RoleBasedTemplateItem[]>>;
  roles: Role[];
  rolesLoading: boolean;
  availableTemplates: EmailTemplate[];
  templatesLoading: boolean;
  handleTemplateSearch: (value: string) => void;
  canEdit: boolean;
  savingEvent: string | null;
  onSave: () => void;
  studyId?: string;
};

const RoleBasedTemplateForm = ({
  items,
  setItems,
  roles,
  rolesLoading,
  availableTemplates,
  templatesLoading,
  handleTemplateSearch,
  canEdit,
  savingEvent,
  onSave,
  studyId,
}: RoleBasedTemplateFormProps) => {
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);

  const addItem = () => {
    setItems([
      ...items,
      {
        template: null,
        isEnabled: true,
        roleId: null,
      },
    ]);
  };

  const removeItem = async (index: number) => {
    const item = items[index];

    // If item has an id, it's an existing template - delete it from the API
    if (item.id && studyId) {
      setDeletingItemId(item.id);
      try {
        await http.delete(
          `/admin/study/${studyId}/email-event-templates/${item.id}`
        );
        toastMessage("success", "Template deleted successfully");
        // Remove from UI after successful deletion
        setItems(items.filter((_, i) => i !== index));
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setDeletingItemId(null);
      }
    } else {
      // Remove from UI immediately for new items (no id)
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (
    index: number,
    field: keyof RoleBasedTemplateItem,
    value: any
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Template {index + 1}
            </Typography>
            {canEdit && items.length > 1 && (
              <IconButton
                size="small"
                onClick={() => removeItem(index)}
                disabled={deletingItemId === item.id}
                sx={{ color: "error.main" }}
              >
                {deletingItemId === item.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>

          <FormControl sx={HalfInputWrapper}>
            <FormLabel sx={LabelStyle}>
              Email Template <span style={{ color: "red" }}>*</span>
            </FormLabel>
            <Autocomplete
              filterOptions={(x) => x}
              onInputChange={(_1: any, value: any, reason: string) => {
                if (reason === "input") {
                  handleTemplateSearch(value);
                }
              }}
              onChange={(_1: any, value: any) => {
                updateItem(index, "template", value);
              }}
              options={availableTemplates}
              value={item.template}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name || ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select email template"
                  inputProps={{
                    ...params.inputProps,
                    readOnly: !canEdit,
                  }}
                />
              )}
              loading={templatesLoading}
              loadingText={<CircularProgress size={20} />}
              noOptionsText="No templates found"
              clearOnBlur={true}
              disabled={!canEdit}
            />
          </FormControl>

          <FormControl sx={HalfInputWrapper}>
            <FormLabel sx={LabelStyle}>Role</FormLabel>
            <Select
              value={item.roleId || ""}
              onChange={(e) =>
                updateItem(index, "roleId", e.target.value || null)
              }
              disabled={!canEdit || rolesLoading}
              displayEmpty
            >
              <MenuItem value="">Default</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={item.isEnabled}
                onChange={(e) =>
                  updateItem(index, "isEnabled", e.target.checked)
                }
                disabled={!canEdit}
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Enable this template
              </Typography>
            }
          />
        </Box>
      ))}

      {canEdit && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addItem}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Template
        </Button>
      )}

      {canEdit && (
        <Box sx={{ ...ModalActionButtonStyles }}>
          {!savingEvent ? (
            <Button
              variant="contained"
              onClick={onSave}
              disabled={
                items.length === 0 || items.some((item) => !item.template)
              }
            >
              Save
            </Button>
          ) : (
            <Box sx={{ height: "36px" }}>
              <CircularProgress size={25} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

type EventAccordionProps = {
  event: { value: string; label: string };
  variables: string[];
  eventTemplateId: string | null;
  selectedTemplate: EmailTemplate | null;
  isEnabled: boolean;
  availableTemplates: EmailTemplate[];
  templatesLoading: boolean;
  handleTemplateSearch: (value: string) => void;
  canEdit: boolean;
  savingEvent: string | null;
  onSave: (
    eventName: string,
    template: EmailTemplate | null,
    isEnabled: boolean
  ) => void;
  onSaveRoleBased: (eventName: string, items: RoleBasedTemplateItem[]) => void;
  studyId?: string;
  roles: Role[];
  roleBasedTemplates: Record<string, RoleBasedTemplateItem[]>;
};

const EventAccordion = ({
  event,
  variables,
  eventTemplateId,
  selectedTemplate,
  isEnabled,
  availableTemplates,
  templatesLoading,
  handleTemplateSearch,
  canEdit,
  savingEvent,
  onSave,
  onSaveRoleBased,
  studyId,
  roles,
  roleBasedTemplates,
}: EventAccordionProps) => {
  const [localTemplate, setLocalTemplate] = useState<EmailTemplate | null>(
    selectedTemplate
  );
  const [localEnabled, setLocalEnabled] = useState<boolean>(isEnabled);
  const [roleBasedItems, setRoleBasedItems] = useState<RoleBasedTemplateItem[]>(
    []
  );
  const [varAnchorEl, setVarAnchorEl] = useState<HTMLElement | null>(null);
  const rolesLoading = false; // Roles are fetched in parent component

  // Enrich localTemplate with dynamicVariables from the fetched dropdown list
  useEffect(() => {
    if (localTemplate && localTemplate.dynamicVariables === undefined) {
      const match = availableTemplates.find((t) => t.id === localTemplate.id);
      if (match?.dynamicVariables !== undefined) {
        setLocalTemplate((prev) =>
          prev ? { ...prev, dynamicVariables: match.dynamicVariables } : null
        );
      }
    }
  }, [availableTemplates, localTemplate]);

  const isRoleBasedEvent =
    event.value === "study_admin.welcome_email" && studyId;

  // Initialize role-based items from existing templates
  useEffect(() => {
    if (isRoleBasedEvent) {
      const existingItems = roleBasedTemplates[event.value] || [];
      if (existingItems.length > 0) {
        setRoleBasedItems(existingItems);
      } else {
        // Start with one empty item
        setRoleBasedItems([
          {
            template: null,
            isEnabled: true,
            roleId: null,
          },
        ]);
      }
    }
  }, [isRoleBasedEvent, event.value, roleBasedTemplates]);

  return (
    <Accordion
      sx={{
        boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.08)",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.08)",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          py: 1.5,
          "& .MuiAccordionSummary-content": {
            my: 1,
          },
          "&.Mui-expanded": {
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            pr: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                color: "text.primary",
              }}
            >
              {event.label}
            </Typography>
            {variables.length > 0 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setVarAnchorEl(e.currentTarget);
                }}
                sx={{ color: "text.secondary", p: 0.25 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </Box>
          <Popover
            open={Boolean(varAnchorEl)}
            anchorEl={varAnchorEl}
            onClose={() => setVarAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: 2, maxWidth: 320 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Available variables — click to copy
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {variables.map((v) => (
                  <Chip
                    key={v}
                    label={`{{${v}}}`}
                    size="small"
                    onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}
                    sx={{ fontFamily: "monospace", cursor: "pointer" }}
                  />
                ))}
              </Box>
            </Box>
          </Popover>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isRoleBasedEvent ? (
              roleBasedItems.filter((item) => item.id).length > 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {roleBasedItems.filter((item) => item.id).length} template
                  {roleBasedItems.filter((item) => item.id).length !== 1
                    ? "s"
                    : ""}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Disabled
                </Typography>
              )
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: isEnabled ? "success.main" : "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  {isEnabled ? "Enabled" : "Disabled"}
                </Typography>
                {localTemplate && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      maxWidth: "250px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    • {localTemplate.name}
                  </Typography>
                )}
                {localTemplate?.dynamicVariables !== undefined &&
                  variables.length > 0 &&
                  (() => {
                    const missing = variables.filter(
                      (v) => !localTemplate!.dynamicVariables!.includes(v)
                    );
                    const extra = localTemplate!.dynamicVariables!.filter(
                      (v) => !variables.includes(v)
                    );
                    const ok = missing.length === 0 && extra.length === 0;
                    const tooltip = ok
                      ? "All variables matched"
                      : [
                          missing.length
                            ? `Missing: ${missing.map((v) => `{{${v}}}`).join(", ")}`
                            : "",
                          extra.length
                            ? `Extra: ${extra.map((v) => `{{${v}}}`).join(", ")}`
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" | ");
                    return ok ? (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: "16px", color: "success.main" }}
                        titleAccess={tooltip}
                      />
                    ) : (
                      <CancelOutlinedIcon
                        sx={{ fontSize: "16px", color: "error.main" }}
                        titleAccess={tooltip}
                      />
                    );
                  })()}
              </>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 3 }}>
        {isRoleBasedEvent ? (
          <RoleBasedTemplateForm
            items={roleBasedItems}
            setItems={setRoleBasedItems}
            roles={roles}
            rolesLoading={rolesLoading}
            availableTemplates={availableTemplates}
            templatesLoading={templatesLoading}
            handleTemplateSearch={handleTemplateSearch}
            canEdit={canEdit}
            savingEvent={savingEvent === event.value ? savingEvent : null}
            onSave={() => onSaveRoleBased(event.value, roleBasedItems)}
            studyId={studyId}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <FormControl sx={HalfInputWrapper}>
              <FormLabel sx={LabelStyle} htmlFor={`template-${event.value}`}>
                Email Template <span style={{ color: "red" }}>*</span>
              </FormLabel>
              <Autocomplete
                filterOptions={(x) => x}
                onInputChange={(_1: any, value: any, reason: string) => {
                  if (reason === "input") {
                    handleTemplateSearch(value);
                  }
                }}
                onChange={(_1: any, value: any) => {
                  setLocalTemplate(value);
                }}
                options={availableTemplates}
                value={localTemplate}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.name || ""
                }
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select email template"
                    inputProps={{
                      ...params.inputProps,
                      readOnly: !canEdit,
                    }}
                  />
                )}
                loading={templatesLoading}
                loadingText={<CircularProgress size={20} />}
                noOptionsText="No templates found"
                clearOnBlur={true}
                disabled={!canEdit}
              />
            </FormControl>

            {localTemplate?.dynamicVariables !== undefined &&
              variables.length > 0 &&
              (() => {
                const missing = variables.filter(
                  (v) => !localTemplate.dynamicVariables!.includes(v)
                );
                const extra = localTemplate.dynamicVariables!.filter(
                  (v) => !variables.includes(v)
                );
                const allMatch = missing.length === 0 && extra.length === 0;
                return (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: allMatch ? "success.light" : "error.light",
                      bgcolor: allMatch
                        ? "rgba(46,125,50,0.06)"
                        : "rgba(211,47,47,0.06)",
                    }}
                  >
                    {allMatch ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: "18px", color: "success.main" }}
                        />
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight={500}
                        >
                          All required variables present
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mb: 0.5,
                          }}
                        >
                          <ErrorOutlineIcon
                            sx={{ fontSize: "18px", color: "error.main" }}
                          />
                          <Typography
                            variant="body2"
                            color="error.main"
                            fontWeight={500}
                          >
                            Variable mismatch detected
                          </Typography>
                        </Box>
                        {missing.length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="error"
                              fontWeight={600}
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Missing in template (platform sends but template
                              won't use):
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {missing.map((v) => (
                                <Chip
                                  key={v}
                                  label={`{{${v}}}`}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontFamily: "monospace" }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {extra.length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="warning.dark"
                              fontWeight={600}
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Extra in template (won't be filled by platform):
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {extra.map((v) => (
                                <Chip
                                  key={v}
                                  label={`{{${v}}}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ fontFamily: "monospace" }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })()}

            <FormControlLabel
              control={
                <Switch
                  checked={localEnabled}
                  onChange={(e) => setLocalEnabled(e.target.checked)}
                  disabled={!canEdit}
                />
              }
              label={
                <Typography variant="body1" fontWeight={500}>
                  Enable this email event
                </Typography>
              }
            />

            {canEdit && (
              <Box sx={{ ...ModalActionButtonStyles }}>
                {savingEvent !== event.value ? (
                  <Button
                    variant="contained"
                    onClick={() =>
                      onSave(event.value, localTemplate, localEnabled)
                    }
                    disabled={!localTemplate}
                  >
                    Save
                  </Button>
                ) : (
                  <Box sx={{ height: "36px" }}>
                    <CircularProgress size={25} />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default EmailConfig;
